import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  FileText,
  Briefcase,
  Users,
  IdCard,
  Heart,
  CreditCard,
  Calendar1Icon,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  UserCheck,
  UserX,
  BarChart3,
  DollarSign,
  FolderOpen,
  IndianRupee,
  Share2,
  Hammer,
} from "lucide-react";
import Apiurl from "../../../Environment/Apiurl";
import axios from "axios";
import { GrStatusInfo } from "react-icons/gr";
// Reuses the same card/info-item styling that EmployeeProfile relies on so the
// two profile pages look and behave identically.
import "../../../assests/css/AttendanceDetails.css";

// ---- Attachment helpers (same logic used in the CarpentersMaster view modal) ----
const getAttachmentUrl = (att) => {
  if (!att) return null;
  if (typeof att === "string" && att.startsWith("http")) return att;
  const p = att.path || att.filename || att;
  if (!p) return null;
  const base = (Apiurl || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  return `${base}/${String(p).replace(/\\/g, "/")}`;
};

const isImageFile = (att) => {
  const name = (att?.filename || att?.path || att || "").toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(name);
};

const isPdfFile = (att) => {
  const name = (att?.filename || att?.path || att || "").toLowerCase();
  return /\.pdf$/.test(name);
};

const CarpenterProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [carpenter, setCarpenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");

  // Attendance states
  const [carpenterAttendance, setCarpenterAttendance] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceMonth, setAttendanceMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  // Payment / wage states
  // NOTE: adjust the field names below (empId / carpenterId) to match whatever
  // your /salary (or /payments) collection actually uses to reference a
  // carpenter — this assumes it's keyed the same way employee salary is.
  const [carpenterPayments, setCarpenterPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState("all");

  // Project states
  const [carpenterProjects, setCarpenterProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectFilter, setProjectFilter] = useState("all");
  const [projectSearch, setProjectSearch] = useState("");

  useEffect(() => {
    fetchCarpenterDetails();
  }, [id]);

  useEffect(() => {
    if (carpenter) {
      if (activeTab === "attendance") fetchCarpenterAttendance();
      if (activeTab === "payments") fetchCarpenterPayments();
      if (activeTab === "projects") fetchCarpenterProjects();
    }
  }, [carpenter, activeTab, attendanceMonth]);

  const fetchCarpenterDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/carpenters/${id}`);
      const data = response.data?.data || response.data;
      setCarpenter(data);
    } catch (err) {
      console.error("❌ Error fetching carpenter details:", err);
      setError("Failed to load carpenter details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCarpenterAttendance = async () => {
    setAttendanceLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/attendance`);
      const allAttendance = response.data;
      const filtered = allAttendance.filter(
        (att) =>
          att.employeeName === carpenter.fullName ||
          att.employeeId === carpenter.carpenterId,
      );
      setCarpenterAttendance(filtered);
    } catch (err) {
      console.error("❌ Error fetching carpenter attendance:", err);
    } finally {
      setAttendanceLoading(false);
    }
  };

  const fetchCarpenterPayments = async () => {
    setPaymentsLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/salary`);
      const allPayments = response.data;
      const filtered = allPayments.filter(
        (p) => p.empId === carpenter.carpenterId,
      );
      const monthOrder = {
        January: 1,
        February: 2,
        March: 3,
        April: 4,
        May: 5,
        June: 6,
        July: 7,
        August: 8,
        September: 9,
        October: 10,
        November: 11,
        December: 12,
      };
      filtered.sort((a, b) => {
        if (a.salaryYear !== b.salaryYear) return b.salaryYear - a.salaryYear;
        return monthOrder[b.salaryMonth] - monthOrder[a.salaryMonth];
      });
      setCarpenterPayments(filtered);
    } catch (err) {
      console.error("❌ Error fetching carpenter payments:", err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fetchCarpenterProjects = async () => {
    setProjectsLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/project`);
      const allProjects = response.data;
      const filtered = allProjects.filter(
        (proj) =>
          proj.carpenterName === carpenter.fullName ||
          proj.carpenterId === carpenter.carpenterId,
      );
      setCarpenterProjects(filtered);
    } catch (err) {
      console.error("❌ Error fetching carpenter projects:", err);
    } finally {
      setProjectsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch {
      return "N/A";
    }
  };

  const getDisplayValue = (value) => {
    if (value === undefined || value === null || value === "") return "N/A";
    return value;
  };

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  const getProjectStatusColor = (status) => {
    const m = {
      Active: { bg: "#dcfce7", color: "#14532d" },
      Completed: { bg: "#dbeafe", color: "#1e3a8a" },
      "On Hold": { bg: "#fef9c3", color: "#713f12" },
      Closed: { bg: "#f1f5f9", color: "#475569" },
    };
    return m[status] || { bg: "#f1f5f9", color: "#475569" };
  };

  const getFilteredProjects = () => {
    let filtered = [...carpenterProjects];
    if (projectFilter === "active")
      filtered = filtered.filter((p) => p.status === "Active");
    else if (projectFilter === "completed")
      filtered = filtered.filter((p) => p.status === "Completed");
    else if (projectFilter === "onhold")
      filtered = filtered.filter((p) => p.status === "On Hold");
    if (projectSearch) {
      const s = projectSearch.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.projectName?.toLowerCase().includes(s) ||
          p.projectId?.toLowerCase().includes(s) ||
          p.clientName?.toLowerCase().includes(s),
      );
    }
    return filtered;
  };

  const getFilteredPayments = () => {
    let filtered = [...carpenterPayments];
    if (paymentFilter === "paid")
      filtered = filtered.filter((s) => s.status === "Paid");
    else if (paymentFilter === "pending")
      filtered = filtered.filter((s) => s.status === "Pending");
    return filtered;
  };

  // ---- Attendance helpers (identical logic to EmployeeProfile) ----
  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split("-").map(Number);
    return new Date(year, month - 1).toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
  };
  const getDayName = (dateStr) => {
    const d = new Date(dateStr);
    return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
  };
  const isWeekend = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDay();
    return day === 0 || day === 6;
  };
  const formatTime12Hr = (time) => {
    if (!time) return "-";
    const ampmMatch = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (ampmMatch) return time;
    const [h, m] = time.split(":").map(Number);
    if (isNaN(h) || isNaN(m)) return "-";
    const period = h >= 12 ? "PM" : "AM";
    const hour = h % 12 === 0 ? 12 : h % 12;
    return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
  };

  const getAttendanceData = () => {
    const [year, month] = attendanceMonth.split("-").map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);

    const firstDateStr = formatDate(firstDay.toISOString().split("T")[0]);
    const lastDateStr = formatDate(lastDay.toISOString().split("T")[0]);

    const monthAttendance = carpenterAttendance.filter((att) => {
      const attDate = att.date;
      return attDate >= firstDateStr && attDate <= lastDateStr;
    });

    const dates = [];
    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      dates.push(formatDate(d.toISOString().split("T")[0]));
    }

    const attendanceMap = {};
    monthAttendance.forEach((att) => {
      attendanceMap[att.date] = att;
    });

    let present = 0;
    let absent = 0;
    let totalWorkingDays = 0;
    let totalWorkingHours = 0;
    let totalOT = 0;

    const dailyData = dates.map((date) => {
      const isWeekendDay = isWeekend(date);
      if (!isWeekendDay) totalWorkingDays++;

      const att = attendanceMap[date];
      const isPresent = !!att;

      if (isPresent) {
        present++;
        if (att.workingHours) {
          const match = att.workingHours.match(/(\d+)h\s*(\d+)?m?/);
          if (match)
            totalWorkingHours +=
              (parseInt(match[1]) || 0) + (parseInt(match[2]) || 0) / 60;
        }
        if (att.ot) {
          const match = att.ot.match(/(\d+)h\s*(\d+)?m?/);
          if (match)
            totalOT +=
              (parseInt(match[1]) || 0) + (parseInt(match[2]) || 0) / 60;
        }
      } else if (!isWeekendDay) {
        absent++;
      }

      return {
        date,
        day: getDayName(date),
        isWeekend: isWeekendDay,
        isPresent,
        attendance: att,
        status: isWeekendDay ? "Weekend" : isPresent ? "Present" : "Absent",
      };
    });

    const attendancePercentage =
      totalWorkingDays > 0
        ? ((present / totalWorkingDays) * 100).toFixed(1)
        : 0;

    return {
      dailyData,
      summary: {
        totalDays: dates.length,
        workingDays: totalWorkingDays,
        present,
        absent,
        attendancePercentage,
        totalWorkingHours: totalWorkingHours.toFixed(1),
        totalOT: totalOT.toFixed(1),
      },
    };
  };

  const attendanceData = getAttendanceData();

  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
    monthOptions.push({ value, label });
  }

  if (loading) {
    return (
      <div className="page">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <div className="text-center">
            <div
              className="spinner-border"
              role="status"
              style={{ width: "3rem", height: "3rem", color: "#00b4db" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading carpenter profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !carpenter) {
    return (
      <div className="page">
        <div className="container-fluid py-5">
          <div
            className="alert alert-danger text-center mx-auto"
            style={{ maxWidth: "600px" }}
          >
            <h4 className="alert-heading">Error</h4>
            <p className="mb-3">{error || "Carpenter not found"}</p>
            <button className="primary-btn" onClick={() => navigate(-1)}>
              <ArrowLeft size={18} />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const attachments = Array.isArray(carpenter.attachments)
    ? carpenter.attachments
    : [];
  const firstImage = attachments.find((a) => isImageFile(a));
  const hasProfilePicture = !!firstImage;
  const profileUrl = firstImage ? getAttachmentUrl(firstImage) : null;

  const totalPayments = carpenterPayments.length;
  const filteredProjects = getFilteredProjects();
  const filteredPayments = getFilteredPayments();

  return (
    <div className="page">
      <div className="container-fluid">
        {/* Tab Navigation */}
        <div className="row g-3 mb-3">
          <div className="col-12">
            <div className="table-card p-2">
              <div
                className="d-flex gap-2 flex-wrap"
                style={{ borderBottom: "1px solid #e5e7eb" }}
              >
                {[
                  {
                    key: "profile",
                    label: "Profile Info",
                    icon: <User size={16} className="me-2" />,
                  },
                  {
                    key: "attendance",
                    label: "Attendance",
                    icon: <BarChart3 size={16} className="me-2" />,
                  },
                  {
                    key: "payments",
                    label: `Payments (${carpenterPayments.length})`,
                    icon: <DollarSign size={16} className="me-2" />,
                  },
                  {
                    key: "projects",
                    label: `Projects (${carpenterProjects.length})`,
                    icon: <FolderOpen size={16} className="me-2" />,
                  },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    className={`btn ${activeTab === tab.key ? "primary-btn text-white" : "btn-outline-secondary"}`}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: "8px 20px",
                      borderRadius: "8px",
                      fontWeight: "500",
                      fontSize: "14px",
                      border: "none",
                      background:
                        activeTab === tab.key ? "#00b4db" : "transparent",
                      color: activeTab === tab.key ? "#fff" : "#6b7280",
                    }}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ==================== PROFILE TAB ==================== */}
        {activeTab === "profile" && (
          <div className="row g-3">
            {/* LEFT SIDEBAR - STAFF DETAILS */}
            <div className="col-lg-3 col-md-4">
              <div className="staff-details-card">
                <h3 className="card-title">Carpenter Details</h3>

                <div
                  className="mx-auto mb-4 border border-3 rounded-circle"
                  style={{
                    width: "140px",
                    height: "140px",
                    overflow: "hidden",
                    borderColor: "#e6f4ef",
                  }}
                >
                  {hasProfilePicture ? (
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <img
                        src={profileUrl}
                        alt={carpenter.fullName}
                        className="w-100 h-100"
                        style={{
                          objectFit: "cover",
                          display: imageError ? "none" : "block",
                        }}
                        onLoad={() => setImageError(false)}
                        onError={() => setImageError(true)}
                      />
                      {imageError && (
                        <div
                          className="d-flex align-items-center justify-content-center w-100 h-100 position-absolute top-0 start-0"
                          style={{ background: "#e6f4ef" }}
                        >
                          <User size={60} style={{ color: "#1ea97c" }} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center w-100 h-100"
                      style={{ background: "#e6f4ef" }}
                    >
                      <User size={60} style={{ color: "#1ea97c" }} />
                    </div>
                  )}
                </div>

                <div className="staff-info-item">
                  <div className="info-icon">
                    <GrStatusInfo size={20} />
                  </div>
                  <div className="info-content">
                    {getDisplayValue(carpenter.status)}
                  </div>
                </div>

                <div className="staff-info-item">
                  <div className="info-icon">
                    <User size={20} />
                  </div>
                  <div className="info-content">
                    <strong>{getDisplayValue(carpenter.fullName)}</strong>
                  </div>
                </div>

                <div className="staff-info-item">
                  <div className="info-icon">
                    <Phone size={20} />
                  </div>
                  <div className="info-content">
                    {getDisplayValue(carpenter.phone)}
                  </div>
                </div>

                <div className="staff-info-item">
                  <div className="info-icon">
                    <IdCard size={20} />
                  </div>
                  <div className="info-content">
                    {getDisplayValue(carpenter.carpenterId)}
                  </div>
                </div>

                <div className="staff-info-item">
                  <div className="info-icon">
                    <Hammer size={20} />
                  </div>
                  <div className="info-content">
                    {getDisplayValue(carpenter.carpenterType)}
                  </div>
                </div>

                <div className="staff-info-item">
                  <div className="info-icon">
                    <IndianRupee size={20} />
                  </div>
                  <div className="info-content">
                    {carpenter.wageType ? (
                      <span
                        style={{
                          background: "#fef3c7",
                          color: "#92400e",
                          padding: "2px 12px",
                          borderRadius: "12px",
                          fontWeight: "600",
                          fontSize: "13px",
                        }}
                      >
                        {carpenter.wageType}
                        {carpenter.rate ? ` · ${fmt(carpenter.rate)}` : ""}
                      </span>
                    ) : (
                      "N/A"
                    )}
                  </div>
                </div>

                {carpenter.referral === "Yes" &&
                  carpenter.referredPersonName && (
                    <div className="staff-info-item">
                      <div className="info-icon">
                        <Share2 size={20} style={{ color: "#2563eb" }} />
                      </div>
                      <div className="info-content">
                        <span style={{ color: "#2563eb" }}>
                          Referred by {carpenter.referredPersonName}
                        </span>
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* MIDDLE SECTION - PROFILE INFO */}
            <div className="col-lg-5 col-md-8">
              <div className="table-card p-4">
                <h5
                  className="fw-bold mb-4 pb-3 border-bottom"
                  style={{ color: "#1ea97c", fontSize: "18px" }}
                >
                  Profile Information
                </h5>

                <div className="d-flex flex-column gap-2">
                  {[
                    [
                      "Branch",
                      typeof carpenter.branch === "object"
                        ? carpenter.branch?.branchName
                        : carpenter.branch,
                    ],
                    ["Date of Birth", formatDate(carpenter.dateOfBirth)],
                    ["Date of Joining", formatDate(carpenter.dateOfJoining)],
                    [
                      "Experience",
                      carpenter.experienceYears
                        ? `${carpenter.experienceYears} yr${carpenter.experienceYears > 1 ? "s" : ""}`
                        : null,
                    ],
                    ["Mobile", carpenter.phone],
                    ["Alternate Phone", carpenter.alternative_Phno],
                    ["Email", carpenter.email],
                    ["Aadhar No.", carpenter.aadhar_No],
                    ["Skill Set", carpenter.skillSet],
                    ["Skill Level", carpenter.skillLevel],
                    ["Wage Type", carpenter.wageType],
                    ["Rate", carpenter.rate ? fmt(carpenter.rate) : null],
                    ["Carpenter Type", carpenter.carpenterType],
                  ].map(([label, value], idx, arr) => (
                    <div
                      key={label}
                      className={`d-flex justify-content-between align-items-start py-2 ${idx !== arr.length - 1 ? "border-bottom" : ""}`}
                    >
                      <span
                        className="fw-semibold"
                        style={{ minWidth: "140px" }}
                      >
                        {label}
                      </span>
                      <span
                        className="text-secondary text-end flex-grow-1"
                        style={{ wordBreak: "break-word" }}
                      >
                        {getDisplayValue(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {carpenter.referral === "Yes" && (
                <div className="table-card p-4 mt-3">
                  <h5
                    className="fw-bold mb-4 pb-3 border-bottom"
                    style={{ color: "#1ea97c", fontSize: "18px" }}
                  >
                    Referral Details
                  </h5>
                  <div className="d-flex flex-column gap-2">
                    {[
                      ["Referred Person Type", carpenter.referredPersonType],
                      ["Referred Person Name", carpenter.referredPersonName],
                      ["Referred Person Phone", carpenter.referredPersonPhone],
                      ["Referred Person Email", carpenter.referredPersonEmail],
                    ].map(([label, value], idx, arr) => (
                      <div
                        key={label}
                        className={`d-flex justify-content-between align-items-start py-2 ${idx !== arr.length - 1 ? "border-bottom" : ""}`}
                      >
                        <span
                          className="fw-semibold"
                          style={{ minWidth: "140px" }}
                        >
                          {label}
                        </span>
                        <span className="text-secondary text-end flex-grow-1">
                          {getDisplayValue(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT SECTION - ATTACHMENTS & ADDITIONAL INFO */}
            <div className="col-lg-4">
              <div className="table-card p-4 mb-3">
                <h5
                  className="fw-bold mb-4 pb-3 border-bottom"
                  style={{ color: "#1ea97c", fontSize: "18px" }}
                >
                  <FileText
                    size={20}
                    className="me-2"
                    style={{ verticalAlign: "middle" }}
                  />
                  Attachments ({attachments.length})
                </h5>

                <div
                  className="rounded p-3"
                  style={{ minHeight: "170px", background: "#f5f6f8" }}
                >
                  {attachments.length > 0 ? (
                    <div className="row g-3">
                      {attachments.map((att, index) => {
                        const fileUrl = getAttachmentUrl(att);
                        const name =
                          att?.filename || att?.path || `file-${index + 1}`;
                        const isImage = isImageFile(att);
                        const isPdf = isPdfFile(att);

                        return (
                          <div key={index} className="col-12">
                            <div className="card shadow-sm p-2">
                              <div className="d-flex align-items-center gap-3">
                                <div
                                  style={{
                                    width: 60,
                                    height: 60,
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: "6px",
                                    border: "1px solid #dee2e6",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                    flexShrink: 0,
                                  }}
                                >
                                  {isImage ? (
                                    <img
                                      src={fileUrl}
                                      alt={name}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : isPdf ? (
                                    <FileText
                                      size={28}
                                      className="text-danger"
                                    />
                                  ) : (
                                    <FileText
                                      size={28}
                                      className="text-primary"
                                    />
                                  )}
                                </div>

                                <div className="flex-grow-1">
                                  <div
                                    className="fw-semibold"
                                    style={{
                                      fontSize: "13px",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {name}
                                  </div>
                                </div>

                                <div className="d-flex gap-2">
                                  <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                    title="View"
                                  >
                                    <Eye size={16} />
                                  </a>
                                  <a
                                    href={fileUrl}
                                    download={name}
                                    className="btn btn-sm btn-outline-success"
                                    title="Download"
                                  >
                                    <Download size={16} />
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center py-5">
                      <FileText size={48} className="text-secondary mb-3" />
                      <p className="text-muted mb-0">No attachments uploaded</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="table-card p-4">
                <h5
                  className="fw-bold mb-4 pb-3 border-bottom"
                  style={{ color: "#1ea97c", fontSize: "18px" }}
                >
                  Additional Details
                </h5>

                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span className="fw-semibold small d-flex align-items-center gap-2">
                      <CreditCard size={16} />
                      App Access
                    </span>
                    <span className="text-secondary small text-end">
                      {getDisplayValue(carpenter.appAccess)}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span className="fw-semibold small d-flex align-items-center gap-2">
                      <Phone size={16} />
                      Device ID
                    </span>
                    <span className="text-secondary small text-end">
                      {getDisplayValue(carpenter.deviceId)}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span className="fw-semibold small d-flex align-items-center gap-2">
                      <Calendar1Icon size={16} />
                      Created At
                    </span>
                    <span className="text-secondary small text-end">
                      {formatDate(carpenter.createdAt)}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center py-2">
                    <span className="fw-semibold small d-flex align-items-center gap-2">
                      <Calendar1Icon size={16} />
                      Last Updated
                    </span>
                    <span className="text-secondary small text-end">
                      {formatDate(carpenter.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== ATTENDANCE TAB ==================== */}
        {activeTab === "attendance" && (
          <div className="row g-3">
            <div className="col-12">
              <div className="table-card p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
                  <h5
                    className="fw-bold"
                    style={{ color: "#1ea97c", fontSize: "18px" }}
                  >
                    <BarChart3
                      size={20}
                      className="me-2"
                      style={{ verticalAlign: "middle" }}
                    />
                    Attendance Record of {carpenter.fullName}
                  </h5>

                  <div className="dropdown white-pill">
                    <button
                      className="dropdown-toggle custom-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                    >
                      <Calendar size={14} /> {getMonthName(attendanceMonth)}
                    </button>
                    <ul
                      className="dropdown-menu"
                      style={{ maxHeight: "260px", overflowY: "auto" }}
                    >
                      {monthOptions.map((opt) => (
                        <li key={opt.value}>
                          <button
                            className="dropdown-item"
                            onClick={() => setAttendanceMonth(opt.value)}
                          >
                            {opt.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-3 col-sm-6">
                    <div
                      className="stat-card"
                      style={{
                        background: "linear-gradient(135deg, #e0f7fa, #b2ebf2)",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "1px solid #4dd0e1",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h6
                            style={{
                              fontSize: "12px",
                              color: "#006064",
                              margin: 0,
                              fontWeight: "600",
                            }}
                          >
                            Working Days
                          </h6>
                          <h3
                            style={{
                              fontSize: "24px",
                              fontWeight: "700",
                              color: "#006064",
                              margin: "4px 0 0",
                            }}
                          >
                            {attendanceData.summary.workingDays}
                          </h3>
                        </div>
                        <Calendar size={28} color="#00838f" />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div
                      className="stat-card"
                      style={{
                        background: "linear-gradient(135deg, #e8f5e9, #c8e6c9)",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "1px solid #81c784",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h6
                            style={{
                              fontSize: "12px",
                              color: "#1b5e20",
                              margin: 0,
                              fontWeight: "600",
                            }}
                          >
                            Present
                          </h6>
                          <h3
                            style={{
                              fontSize: "24px",
                              fontWeight: "700",
                              color: "#1b5e20",
                              margin: "4px 0 0",
                            }}
                          >
                            {attendanceData.summary.present}
                          </h3>
                        </div>
                        <UserCheck size={28} color="#2e7d32" />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div
                      className="stat-card"
                      style={{
                        background: "linear-gradient(135deg, #fce4ec, #f8bbd0)",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "1px solid #ef9a9a",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h6
                            style={{
                              fontSize: "12px",
                              color: "#b71c1c",
                              margin: 0,
                              fontWeight: "600",
                            }}
                          >
                            Absent
                          </h6>
                          <h3
                            style={{
                              fontSize: "24px",
                              fontWeight: "700",
                              color: "#b71c1c",
                              margin: "4px 0 0",
                            }}
                          >
                            {attendanceData.summary.absent}
                          </h3>
                        </div>
                        <UserX size={28} color="#c62828" />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div
                      className="stat-card"
                      style={{
                        background: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "1px solid #ffb74d",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h6
                            style={{
                              fontSize: "12px",
                              color: "#e65100",
                              margin: 0,
                              fontWeight: "600",
                            }}
                          >
                            Attendance %
                          </h6>
                          <h3
                            style={{
                              fontSize: "24px",
                              fontWeight: "700",
                              color: "#e65100",
                              margin: "4px 0 0",
                            }}
                          >
                            {attendanceData.summary.attendancePercentage}%
                          </h3>
                        </div>
                        <Users size={28} color="#ef6c00" />
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="d-flex gap-4 mb-4 flex-wrap"
                  style={{
                    padding: "12px 16px",
                    background: "#f8fafc",
                    borderRadius: "8px",
                  }}
                >
                  <span style={{ fontSize: "13px", color: "#374151" }}>
                    Total Working Hours:{" "}
                    <strong>{attendanceData.summary.totalWorkingHours}h</strong>
                  </span>
                  <span style={{ fontSize: "13px", color: "#374151" }}>
                    Total OT: <strong>{attendanceData.summary.totalOT}h</strong>
                  </span>
                </div>

                {attendanceLoading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border"
                      role="status"
                      style={{ color: "#00b4db" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading attendance...</p>
                  </div>
                ) : attendanceData.dailyData.length === 0 ? (
                  <div className="text-center py-5">
                    <Calendar size={48} className="text-secondary mb-3" />
                    <p className="text-muted mb-0">
                      No attendance records found for this month
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="common-table">
                      <thead>
                        <tr>
                          <th>SNO</th>
                          <th>Date</th>
                          <th>Day</th>
                          <th>Status</th>
                          <th>In Time</th>
                          <th>Out Time</th>
                          <th>WH</th>
                          <th>OT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceData.dailyData.map((day, index) => {
                          const isPresent = day.isPresent;
                          const att = day.attendance;
                          let displayStatus = day.status;
                          let bg = "#d1fae5",
                            color = "#065f46";
                          if (day.isWeekend) {
                            bg = "#e8f5e9";
                            color = "#2e7d32";
                          } else if (!isPresent) {
                            bg = "#f3f4f6";
                            color = "#374151";
                          }
                          return (
                            <tr
                              key={day.date}
                              style={day.isWeekend ? { opacity: 0.6 } : {}}
                            >
                              <td>{index + 1}</td>
                              <td>{day.date}</td>
                              <td>{day.day}</td>
                              <td>
                                <span
                                  style={{
                                    background: bg,
                                    color: color,
                                    padding: "4px 12px",
                                    borderRadius: "20px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {displayStatus}
                                </span>
                              </td>
                              <td>
                                {isPresent ? formatTime12Hr(att?.inTime) : "-"}
                              </td>
                              <td>
                                {isPresent ? formatTime12Hr(att?.outTime) : "-"}
                              </td>
                              <td>
                                {isPresent ? att?.workingHours || "-" : "-"}
                              </td>
                              <td>{isPresent && att?.ot ? att.ot : "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ==================== PAYMENTS TAB ==================== */}
        {activeTab === "payments" && (
          <div className="row g-3">
            <div className="col-12">
              <div className="table-card p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
                  <h5
                    className="fw-bold"
                    style={{ color: "#1ea97c", fontSize: "18px" }}
                  >
                    <DollarSign
                      size={20}
                      className="me-2"
                      style={{ verticalAlign: "middle" }}
                    />
                    Payment History of {carpenter.fullName}
                  </h5>

                  <div className="d-flex gap-2 flex-wrap">
                    {["all", "paid", "pending"].map((f) => (
                      <button
                        key={f}
                        className={`btn ${paymentFilter === f ? "primary-btn text-white" : "btn-outline-secondary"}`}
                        onClick={() => setPaymentFilter(f)}
                        style={{
                          padding: "4px 16px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "500",
                          border: "none",
                          background:
                            paymentFilter === f ? "#00b4db" : "transparent",
                          color: paymentFilter === f ? "#fff" : "#6b7280",
                          textTransform: "capitalize",
                        }}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                {paymentsLoading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border"
                      role="status"
                      style={{ color: "#00b4db" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">
                      Loading payment records...
                    </p>
                  </div>
                ) : filteredPayments.length === 0 ? (
                  <div className="text-center py-5">
                    <DollarSign size={48} className="text-secondary mb-3" />
                    <p className="text-muted mb-0">
                      No payment records found for this carpenter
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="common-table">
                      <thead>
                        <tr>
                          <th>SNO</th>
                          <th>Month / Year</th>
                          <th>Amount</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPayments.map((p, index) => (
                          <tr key={p._id}>
                            <td>{index + 1}</td>
                            <td style={{ fontWeight: "600" }}>
                              {p.salaryMonth} {p.salaryYear}
                            </td>
                            <td style={{ fontWeight: "700", color: "#1d4ed8" }}>
                              {fmt(p.netSalary)}
                            </td>
                            <td>
                              <span
                                className={`status ${(p.status || "").toLowerCase()}`}
                                style={{ padding: "3px 10px", fontSize: 12 }}
                              >
                                <CheckCircle size={11} /> {p.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ==================== PROJECTS TAB ==================== */}
        {activeTab === "projects" && (
          <div className="row g-3">
            <div className="col-12">
              <div className="table-card p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
                  <h5
                    className="fw-bold"
                    style={{ color: "#1ea97c", fontSize: "18px" }}
                  >
                    <FolderOpen
                      size={20}
                      className="me-2"
                      style={{ verticalAlign: "middle" }}
                    />
                    Projects Associated with {carpenter.fullName}
                  </h5>

                  <div className="d-flex gap-2 flex-wrap">
                    {[
                      ["all", "All"],
                      ["active", "Active"],
                      ["completed", "Completed"],
                      ["onhold", "On Hold"],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        className={`btn ${projectFilter === key ? "primary-btn text-white" : "btn-outline-secondary"}`}
                        onClick={() => setProjectFilter(key)}
                        style={{
                          padding: "4px 16px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "500",
                          border: "none",
                          background:
                            projectFilter === key ? "#00b4db" : "transparent",
                          color: projectFilter === key ? "#fff" : "#6b7280",
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                  <div
                    className="search-box white-pill d-flex align-items-center"
                    style={{ maxWidth: "300px" }}
                  >
                    <Search size={16} />
                    <input
                      placeholder="Search projects..."
                      value={projectSearch}
                      onChange={(e) => setProjectSearch(e.target.value)}
                      style={{
                        fontSize: "13px",
                        padding: "6px 10px",
                        border: "none",
                        outline: "none",
                        width: "100%",
                      }}
                    />
                  </div>
                </div>

                {projectsLoading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border"
                      role="status"
                      style={{ color: "#00b4db" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading projects...</p>
                  </div>
                ) : filteredProjects.length === 0 ? (
                  <div className="text-center py-5">
                    <FolderOpen size={48} className="text-secondary mb-3" />
                    <p className="text-muted mb-0">
                      No projects found for this carpenter
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="common-table">
                      <thead>
                        <tr>
                          <th>SNO</th>
                          <th>Project ID</th>
                          <th>Project Name</th>
                          <th>Client</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Start Date</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProjects.map((proj, index) => {
                          const statusColor = getProjectStatusColor(
                            proj.status,
                          );
                          return (
                            <tr key={proj._id}>
                              <td>{index + 1}</td>
                              <td>
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#1e40af",
                                    background: "#dbeafe",
                                    padding: "2px 8px",
                                    borderRadius: 8,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {proj.projectId || "N/A"}
                                </span>
                              </td>
                              <td style={{ fontWeight: "500" }}>
                                {proj.projectName}
                              </td>
                              <td>{proj.clientName || "N/A"}</td>
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    background: "#e0f2fe",
                                    color: "#0369a1",
                                    fontSize: "11px",
                                    padding: "2px 8px",
                                  }}
                                >
                                  {proj.projectType || "—"}
                                </span>
                              </td>
                              <td>
                                <span
                                  style={{
                                    padding: "2px 12px",
                                    borderRadius: "12px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    backgroundColor: statusColor.bg,
                                    color: statusColor.color,
                                  }}
                                >
                                  {proj.status}
                                </span>
                              </td>
                              <td
                                style={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatDate(proj.startDate)}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() =>
                                    navigate(`/projects/track/${proj._id}`)
                                  }
                                  style={{
                                    padding: "2px 10px",
                                    fontSize: "11px",
                                    borderRadius: "6px",
                                  }}
                                >
                                  <Eye size={14} className="me-1" />
                                  Track
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* BACK BUTTON */}
      <div className="mt-3">
        <button
          className="btn bg-secondary text-white d-flex align-items-center gap-2"
          onClick={() => navigate("/craftsmen/craftsmen-master")}
        >
          <ArrowLeft size={18} />
          Back to Carpenter List
        </button>
      </div>
    </div>
  );
};

export default CarpenterProfile;
